import {
	type ActorRefFrom,
	assign,
	sendTo,
	setup,
	type StateFrom,
} from 'xstate';

import type { Cart, DeliveryOption } from 'models/api';

import type {
	DeliveryMachineContext,
	DeliveryMachineEvents,
	DeliveryMachineInputs,
} from './delivery.types';

export const deliveryMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: DeliveryMachineContext;
		events: DeliveryMachineEvents;
		input: DeliveryMachineInputs;
	},
	actions: {
		updateData: assign({
			cart: (_, params: { cart: Cart | undefined }) => params.cart,
			availableDeliveryMethods: (_, params) =>
				params.cart?.availableDeliveryMethods,
			selectedDelivery: (_, params) => params.cart?.selectedDelivery,
			errors: (_, params) => params.cart?.errorList,
		}),

		saveSelectedDeliveryToContext: assign({
			selectedDelivery: (_, params: { deliveryOption: DeliveryOption }) =>
				params.deliveryOption,
		}),

		resetToOldDelivery: assign({
			selectedDelivery: ({ context }) => context.oldSelectedDelivery,
		}),
		sendSelectedPickupToParent: sendTo(
			({ context }) => context.checkoutMachineRef,
			(_, params: { selectedDeliveryPickup: DeliveryOption }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					selectedDelivery: params.selectedDeliveryPickup,
				},
			}),
		),
		sendSelectedDeliveryToParent: sendTo(
			({ context }) => context.checkoutMachineRef,
			(_, params: { selectedDelivery: DeliveryOption | undefined }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					selectedDelivery: params.selectedDelivery,
				},
			}),
		),
	},
}).createMachine({
	id: 'delivery',
	initial: 'idle',

	context: ({ input: { checkoutMachineRef } }) => ({
		checkoutMachineRef,
	}),
	states: {
		idle: {
			id: 'idle',
			on: {
				CART_UPDATED: {
					target: '#idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
				SELECT_DELIVERY_PICKUP: {
					target: 'loading',
					actions: {
						type: 'sendSelectedPickupToParent',
						params: ({ event }) => ({
							selectedDeliveryPickup: event.value,
						}),
					},
				},
			},
		},
		debounceDeliveryMethodUpdate: {
			id: 'debounceDeliveryMethodUpdate',
			after: {
				300: {
					target: 'loading',
					actions: {
						type: 'sendSelectedDeliveryToParent',
						params: ({ context }) => ({
							selectedDelivery: context.selectedDelivery,
						}),
					},
				},
			},
		},
		loading: {
			on: {
				CART_UPDATED: {
					target: '#idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
			},
		},
	},
	on: {
		RESET_TO_OLD: {
			target: '#idle',
			actions: 'resetToOldDelivery',
		},
		SELECT_DELIVERY_METHOD: {
			target: '#debounceDeliveryMethodUpdate',
			reenter: true,
			actions: {
				type: 'saveSelectedDeliveryToContext',
				params: ({ event }) => ({ deliveryOption: event.value }),
			},
		},
	},
});
export type DeliveryMachineState = StateFrom<typeof deliveryMachine>;
export type DeliveryMachineActor = ActorRefFrom<typeof deliveryMachine>;
